import type { SnapshotChangeKind } from "@/domains/pm/utils/scheduleSnapshotDiff";

/**
 * The colour and sign vocabulary for a schedule diff.
 *
 * Deliberately the same four tones and the same `+ − ~` signs the IFC model
 * diff uses (`domains/buildings/components/ifc-viewer/diff/diffSummaryShared.ts`)
 * — a reader who has compared two IFC files should not have to learn a second
 * palette to compare two schedules. Restated here rather than imported: PM
 * reaching into the IFC viewer's internals would be the wrong coupling for a
 * shared convention neither owns.
 */
export interface ChangeTone {
  /** Solid fill, for the legend dot and the proportion bar. */
  bar: string;
  /** Text colour for the count. */
  text: string;
  /** Prefix shown before the count. */
  sign: string;
  /**
   * The timeline bar for an issue of this kind.
   *
   * Colour on that chart means the change kind and NOTHING else — not status,
   * not the brand. Painting today's bars in the brand red put them a shade
   * away from the rose an issue gone since the snapshot is drawn in, which is
   * the one distinction the chart exists to make.
   */
  chart: string;
}

export const CHANGE_TONE: Record<SnapshotChangeKind, ChangeTone> = {
  moved: {
    bar: "bg-amber-500",
    text: "text-amber-600",
    sign: "~",
    chart: "border-amber-500/60 bg-amber-500/20",
  },
  added: {
    bar: "bg-emerald-500",
    text: "text-emerald-600",
    sign: "+",
    chart: "border-emerald-500/60 bg-emerald-500/20",
  },
  removed: {
    bar: "bg-rose-500",
    text: "text-rose-600",
    sign: "−",
    chart: "border-dashed border-rose-400 bg-rose-400/10",
  },
  unchanged: {
    bar: "bg-slate-400",
    text: "text-muted-foreground",
    sign: "",
    chart: "border-foreground/25 bg-foreground/10",
  },
};

/** The order the digest and the change list present the kinds in. */
export const CHANGE_KINDS: SnapshotChangeKind[] = ["moved", "added", "removed", "unchanged"];

/**
 * The comparison chart's bar fills.
 *
 * On that chart colour means WHEN, not what kind of change: the snapshot's
 * dates or today's. It repeats what the lane already says (snapshot on top,
 * today beneath), so the legend can name both. Size says nothing — the two
 * bars are the same height, so a reader is never asked to judge a two-pixel
 * difference. The change kind is still legible from the row's leading tone bar
 * and from the Overview beside it.
 */
export const COMPARE_BAR = {
  /** Where the issue sat in the snapshot. */
  baseline: "#cbd5e1",
  /** Where it sits today. */
  today: "#64748b",
} as const;
